import {
  Button,
  Grid,
  Heading,
  IconCheckLine,
  InlineList,
  Pill,
  Text,
  Tooltip,
} from "@instructure/ui";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { FeatureContext, TermsContext } from "../../../../../utils/Context";
import CopyCourseToggle from "./CopyCourseToggle";
import SectionButton from "./SectionButton";
import { dueDateAfterTerm, getDueDate, getTermEndDate } from "./utils";

const SectionHeader = ({
  section,
  cardColor,
  handleSectionSelect,
  reflectionTemplates,
  sectionRefTemplate,
  defaultDeadline,
  copyCourseLoading,
  onCopyCourseToggle,
}) => {
  const { t, i18n } = useTranslation();
  const features = useContext(FeatureContext);
  const { insightsTerms } = useContext(TermsContext);
  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";
  const autofinalizationFlag =
    finalizeFlag &&
    features.find((f) => f.name === "autofinalization_flag")?.value === "TRUE";
  const copyCourseFlag =
    features.find((f) => f.name === "copy_course_flag")?.value === "TRUE";
  const dueDate = dueDateAfterTerm(section, defaultDeadline);

  const finalizationDeadline = getDueDate(section.term, dueDate, insightsTerms);
  const termEndDate = getTermEndDate(
    section.term,
    insightsTerms
  )?.toLocaleDateString();

  // True when today is past the finalization deadline AND autofinalization is enabled.
  // Autofinalization tasks are scheduled for the day AFTER the deadline.
  const isPastFinalizationDeadline =
    autofinalizationFlag &&
    finalizationDeadline != null &&
    new Date() > finalizationDeadline;

  const formattedDueDate =
    finalizationDeadline &&
    new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(finalizationDeadline);

  return (
    <Grid startAt="x-large" rowSpacing="small">
      <Grid.Row>
        <Grid.Col>
          <SectionButton
            aria-label={t("dashboard.offeringCardAria", {
              code: section.offering.code,
              term: section.term?.name,
            })}
            onClick={() => handleSectionSelect(section)}
          >
            <Heading as="h3" level="h4">
              <Text wrap="break-word">
                <span style={{ color: cardColor }}>
                  {section.name ??
                    section.offering.code ??
                    t("dashboard.missingSectionName")}
                </span>
                <InlineList delimiter="pipe">
                  <InlineList.Item>{section.offering.code}</InlineList.Item>
                  <InlineList.Item>{section.term?.name}</InlineList.Item>
                </InlineList>
              </Text>
            </Heading>
          </SectionButton>
        </Grid.Col>
      </Grid.Row>
      {copyCourseFlag && (
        <Grid.Row>
          <Grid.Col>
            <CopyCourseToggle
              section={section}
              effectiveState={section.copy_flag_lock_effective ?? false}
              selfState={section.copy_flag_locked_by_self ?? false}
              lockedByOther={section.copy_flag_locked_by_other ?? false}
              loading={copyCourseLoading}
              onToggle={onCopyCourseToggle}
            />
          </Grid.Col>
        </Grid.Row>
      )}
      {finalizeFlag && (
        <Grid.Row>
          <Grid.Col>
            {section.finalized ? (
              <Pill color="success" renderIcon={<IconCheckLine />}>
                {t("finalizeCourse.finalized")}
              </Pill>
            ) : isPastFinalizationDeadline ? (
              <Tooltip
                renderTip={t("finalizeCourse.autofinalizationInProgress")}
              >
                <Button size="small" disabled>
                  {t("finalizeCourse.finalize")}
                </Button>
              </Tooltip>
            ) : (
              <>
                <Link
                  to="/scores/home"
                  state={{ section, reflectionTemplates, sectionRefTemplate }}
                >
                  <Button size="small">{t("finalizeCourse.finalize")}</Button>
                </Link>
                {formattedDueDate && (
                  <Text
                    as="div"
                    size="small"
                    color="secondary"
                    data-termid={termEndDate}
                  >
                    <Text weight="bold" size="small">
                      {t("resultsDeadline.due")}{" "}
                    </Text>
                    {formattedDueDate}
                  </Text>
                )}
              </>
            )}
          </Grid.Col>
        </Grid.Row>
      )}
    </Grid>
  );
};

export default SectionHeader;
